<template>
    <div>
        <b-row>
            <b-col cols="5">
                <h3>Ingredients</h3>
                <a href="#" @click.prevent="onNewIngredient">
                    <span>New Ingredient</span> <i class="fas fa-plus-circle" />
                </a>
            </b-col>
        </b-row>
        <b-row v-if="showForm">
            <b-col cols="2">
                <label>Drug Source</label>
                <div>{{ drugSourceFormatter(this.upsertDrugSource) }}</div>
            </b-col>
            <b-col cols="3">
                <label>Drug Name</label>
                <div>{{ this.upsertDrugName || 'Loading...' }}</div>
            </b-col>
            <b-col cols="40">
                <fg-input v-model.number="upsertQuantity" :no-wheel="true" label="Quantity" placeholder="0.00"
                    type="number" />
            </b-col>
            <b-col cols="40">
                <fg-input v-model="upsertUnitOfMeasure" label="Unit Of Measure" type="string" />
            </b-col>
            <b-col cols="12">
                <b-button variant="success" @click="submitUpsert">Continue</b-button>
                <b-button @click="onCancelClick">Cancel</b-button>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-table id="ingredients-table" ref="ingredients-table" :fields="fields" :items="ingredients"
                    primary-key="id">
                    <template #cell(drugName)="data">
                        <router-link :to="linkForItem(data.item)">
                            {{ data.value }}
                        </router-link>
                    </template>
                    <template #cell(actions)="data">
                        <div class="text-right">
                            <a href="#" @click.prevent="updateIngredient(data.item)">
                                <b-icon v-b-tooltip="'Edit'" icon="pencil-square" />
                            </a>
                            <a href="#" @click.prevent="deleteConfirmDialog(data.item)">
                                <b-icon v-b-tooltip="'Remove'" class="mx-1" icon="x-circle-fill" variant="danger" />
                            </a>
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>
        <b-modal id="IngredientDrugSelect" hide-footer hide-header size="xl">
            <IngredientSelectModalComponent :current-compound-id="compoundId" @cancel="onIngredientCanceled" @select="onIngredientSelected($event)" />
        </b-modal>
    </div>
</template>

<script lang="ts">

import { Component, Prop, Ref, Vue } from 'vue-property-decorator';
import { Table, TableColumn } from 'element-ui'
import IngredientSelectModalComponent from '@/components/Compound/IngredientSelectModalComponent.vue';
import { CompoundIngredients } from '@/models/Drug/CompoundDrug';
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";
import Axios, { AxiosError } from "axios";
import { BTable } from "bootstrap-vue";
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "IngredientComponent",
    components: {
        IngredientSelectModalComponent,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn
    }
})
export default class IngredientComponent extends Vue {

    @Prop() private compoundId!: number;
    @Prop() private ingredients!: CompoundIngredients[];

    @Ref('ingredients-table') private ingredientsTable!: BTable;

    private showForm: boolean = false;
    private upsertId: number = 0;
    private upsertDrugSource: number = 0;
    private upsertDrugId: number = 0;
    private upsertDrugName: string = "";
    private upsertQuantity: number = 0;
    private upsertUnitOfMeasure: string = "";
    private upsertIngredientRow: CompoundIngredients = new CompoundIngredients;
    private alreadyShipped: boolean = false;
    private drugNamesCache: Map<string, string> = new Map();
    private ndcCache: Map<string, string> = new Map();
    private loadingDrugNames: Set<string> = new Set();

    private fields = [
        // "id",
        { key: "drugSource", formatter: this.drugSourceFormatter },
        { key: "drugName", formatter: this.drugNameFormatter },
        { key: "ndc", label: "NDC", formatter: this.ndcFormatter },
        "quantity",
        "unitOfMeasure",
        { key: "actions", label: "" },
    ]

    drugNameFormatter(value: number, _: string, item: CompoundIngredients) {
        const drugId = item.drugID;
        if (!drugId) return 'Invalid drug.';

        // Return cached value if available
        const cacheKey = `${item.drugSource}_${drugId}`;
        const cachedName = this.drugNamesCache.get(cacheKey);
        if (cachedName) return cachedName;

        // Load the name if not already loading
        if (!this.loadingDrugNames.has(cacheKey)) {
            this.loadDrugName(item);
        }

        return 'Loading...';
    }

    ndcFormatter(value: string, _: string, item: CompoundIngredients) {
        const drugId = item.drugID;
        if (!drugId) return '';

        const cacheKey = `${item.drugSource}_${drugId}`;
        const cachedNdc = this.ndcCache.get(cacheKey);
        if (cachedNdc !== undefined) return cachedNdc;

        // NDC is loaded alongside drug name in loadDrugName
        return 'Loading...';
    }

    async loadDrugName(item: CompoundIngredients) {
        const cacheKey = `${item.drugSource}_${item.drugID}`;
        this.loadingDrugNames.add(cacheKey);

        try {
            const response = await Axios.get<DrugProduct>(`/api/Drug/${item.drugID}`, {
                params: { drugSource: item.drugSource }
            });
            const drugName = response.data.productNameShort;
            this.drugNamesCache.set(cacheKey, drugName);
            item.drugName = drugName;

            const ndc = response.data.ndc || '';
            this.ndcCache.set(cacheKey, ndc);
            item.ndc = ndc;

            // Force table refresh to show the loaded name and NDC
            this.$nextTick(() => {
                if (this.ingredientsTable && this.ingredientsTable.refresh) {
                    this.ingredientsTable.refresh();
                }
            });
        } catch (err) {
            console.warn("Error loading drug name:", err);
            this.drugNamesCache.set(cacheKey, 'Error Getting Name');
            this.ndcCache.set(cacheKey, '');
            item.ndc = '';

            // Force table refresh to show the error
            this.$nextTick(() => {
                if (this.ingredientsTable && this.ingredientsTable.refresh) {
                    this.ingredientsTable.refresh();
                }
            });
        } finally {
            this.loadingDrugNames.delete(cacheKey);
        }
    }

    drugSourceFormatter(drugSource: DrugSource) {
        return DrugSource[drugSource].toString();
    }

    linkForItem(item: CompoundIngredients) {
        let name = '';
        if (item.drugSource == DrugSource.GsddFederal) name = 'Drug';
        if (item.drugSource == DrugSource.NonGsddProduct) name = 'Non-GSDD-Drug';
        return { name, params: { id: item.drugID } };
    }

    reset() {
        this.showForm = false;
        this.upsertId = 0;
        this.upsertDrugSource = 0;
        this.upsertDrugId = 0;
        this.upsertDrugName = "";
        this.upsertQuantity = 0;
        this.upsertUnitOfMeasure = "";
        this.upsertIngredientRow = new CompoundIngredients();
        this.alreadyShipped = false;
        console.log("created");
    }

    onContinue() {
        this.submitUpsert();
        this.$emit('continue');
    }

    submitUpsert() {
        if (this.upsertQuantity < 0.0001) {
            this.$bvModal.msgBoxOk('Quantity can not be zero or less.');
            return;
        }
        this.upsertIngredientRow = new CompoundIngredients();
        this.upsertIngredientRow.id = this.upsertId || 0;
        this.upsertIngredientRow.compoundID = this.compoundId;
        this.upsertIngredientRow.drugSource = this.upsertDrugSource || 0;
        this.upsertIngredientRow.drugID = this.upsertDrugId!;
        this.upsertIngredientRow.quantity = this.upsertQuantity!;
        this.upsertIngredientRow.unitOfMeasure = this.upsertUnitOfMeasure!;

        if (this.ingredients.map(item => item.id).indexOf(this.upsertIngredientRow.id) > -1)
            this.ingredients.splice(this.ingredients.map(item => item.id).indexOf(this.upsertIngredientRow.id), 1);
        this.ingredients.push(this.upsertIngredientRow);
        this.closeForm();
        this.$emit('continue');
    }

    async updateIngredient(item: CompoundIngredients) {
        this.onIngredientAddUpdDelClicked()
        if (!this.alreadyShipped) {
            this.showForm = true;
            this.upsertId = item.id;
            this.upsertDrugSource = item.drugSource;
            this.upsertDrugId = item.drugID;
            this.upsertQuantity = item.quantity;
            this.upsertUnitOfMeasure = item.unitOfMeasure;

            // Load drug name from cache or API
            const cacheKey = `${item.drugSource}_${item.drugID}`;
            const cachedName = this.drugNamesCache.get(cacheKey);
            if (cachedName) {
                this.upsertDrugName = cachedName;
            } else if (item.drugName) {
                this.upsertDrugName = item.drugName;
            } else {
                this.upsertDrugName = "Loading...";
                try {
                    const response = await Axios.get<DrugProduct>(`/api/Drug/${item.drugID}`, {
                        params: { drugSource: item.drugSource }
                    });
                    this.upsertDrugName = response.data.productNameShort;
                    this.drugNamesCache.set(cacheKey, response.data.productNameShort);
                } catch (err) {
                    console.warn("Error loading drug name:", err);
                    this.upsertDrugName = "Error Getting Name";
                }
            }
        } else this.closeForm();
    }

    deleteConfirmDialog(item: CompoundIngredients) {
        this.onIngredientAddUpdDelClicked();
        if (!this.alreadyShipped) {
            this.$bvModal.msgBoxConfirm(`This compound Ingredient will get deleted. Are you sure?`, {
                title: 'Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'YES',
                cancelTitle: 'NO',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
                .then(value => {
                    if (!value) return;
                    this.deleteIngredient(item);
                })
                .catch(err => {
                    console.error("Error caught on Delete button.", err);
                });
        } else this.closeForm();
    }

    deleteIngredient(item: CompoundIngredients) {
        if (!item) return;

        const itemIdType = typeof (item.id);
        if (itemIdType != "number") return;

        if (this.ingredients.map(item => item.id).indexOf(item.id) < 0) return;
        this.ingredients.splice(this.ingredients.map(ci => ci.id).indexOf(item.id), 1);
        this.$emit('delete');
    }

    onNewIngredient() {
        this.onIngredientAddUpdDelClicked();
        this.clearForm();
        if (!this.alreadyShipped) this.$bvModal.show("IngredientDrugSelect");
    }

    openFrom() {
        this.showForm = true;
    }

    closeForm() {
        this.clearForm();
        this.showForm = false;
    }

    clearForm() {
        this.upsertId = Math.min(...this.ingredients.map(item => item.id)) - 1;
        if (this.upsertId > 0) this.upsertId = 0;
        this.upsertDrugSource = 0;
        this.upsertDrugId = 0;
        this.upsertDrugName = "";
        this.upsertQuantity = 0;
        this.upsertUnitOfMeasure = ""
    }

    onCancelClick() {
        this.closeForm();
        this.$emit('cancel');
    }

    async onIngredientSelected(parm1: any) {
        //console.log(parm1);
        var drugSrcId = parm1.split("_", 4)
        this.upsertDrugSource = Number(drugSrcId[0]);
        this.upsertDrugId = Number(drugSrcId[1]);
        this.upsertQuantity = Number(drugSrcId[2]);
        this.upsertUnitOfMeasure = drugSrcId[3];
        this.$bvModal.hide("IngredientDrugSelect");
        var duplicate = this.ingredients.some(obj => obj.drugSource == this.upsertDrugSource && obj.drugID == this.upsertDrugId)
        if (duplicate) {
            this.$bvModal.msgBoxOk('Duplicate DrugSource/DrugId selected.');
            return;
        }

        // Load drug name
        const cacheKey = `${this.upsertDrugSource}_${this.upsertDrugId}`;
        const cachedName = this.drugNamesCache.get(cacheKey);
        if (cachedName) {
            this.upsertDrugName = cachedName;
        } else {
            this.upsertDrugName = "Loading...";
            try {
                const response = await Axios.get<DrugProduct>(`/api/Drug/${this.upsertDrugId}`, {
                    params: { drugSource: this.upsertDrugSource }
                });
                this.upsertDrugName = response.data.productNameShort;
                this.drugNamesCache.set(cacheKey, response.data.productNameShort);
                this.ndcCache.set(cacheKey, response.data.ndc || '');
            } catch (err) {
                console.warn("Error loading drug name:", err);
                this.upsertDrugName = "Error Getting Name";
            }
        }

        this.submitUpsert();
        this.$emit('continue');
    }

    onIngredientCanceled() {
        this.$bvModal.hide("IngredientDrugSelect");
    }

    async onIngredientAddUpdDelClicked() {
        let shippedSearchResults: number | null = null;
        let sc: number = 0;
        let scGood: number[] = [200, 204];
        await this.fetchShippedSearch(this.compoundId!)
            .then(value => {
                if (value.length == null) sc = value;
                else {
                    sc = value[0];
                    shippedSearchResults = value[1];
                }
            });
        if (!scGood.includes(sc)) {
            this.$bvModal.msgBoxOk(`Database error occurred while attempting to search if compound has a prescription shipped`);
            return false;
        }
        if (shippedSearchResults != null && shippedSearchResults != "") {
            this.$bvModal.msgBoxOk(`Ingredients can't be changed as compound already has a shipped prescription`);
            this.alreadyShipped = true;
        } else this.alreadyShipped = false;
    }

    async fetchShippedSearch(compoundId: number): Promise<[number, number | null] | any> {
        try {
            const response = await this.$http.get(`/Compound/prescription-shipped-search?compoundid=${compoundId.toString()}`);
            const shippedFound: number | null = response.data;
            const sc: number = Number(response.status);
            return [sc, shippedFound];
        } catch (err) {
            const error = err as AxiosError;
            console.error('Error while searching if compound has a shipped prescription order.', {
                error,
                response: error?.response
            });
            this.$notification(NotificationOptions.error(err));
            return error.response?.status;
        }
    }

}
</script>

<style scoped></style>
